import type { JSX } from 'react';
import { DateTime } from '@ksyos/date-time';
import { Card, SectionHeader } from '@ksyos/design-system';
import { orderBy } from 'lodash';
import { useEffect, useState } from 'react';
import type { ServiceCode } from '@common/enums';
import type { Process } from '@common/interfaces';
import type { IMonitoringChartItem } from '@frontend/components/pages/monitoring/MonitoringChart';
import { useVanDerLaarDistributionFromDate } from '@common/constants';
import { TotalWeightLossState } from '@common/enums/totalWeightLossState';
import { TotalWeightLossErrorCode } from '@common/interfaces/bariatrics/totalWeightLossErrorCodes';
import { MonitoringCardFooter } from '@frontend/components/pages/monitoring/MonitoringCardFooter';
import { MonitoringChart } from '@frontend/components/pages/monitoring/MonitoringChart';
import { getTotalWeightLoss } from '@frontend/services/api/bariatricsApiService';
import { canReadBariPostTotalWeightLoss } from '@frontend/services/authorizationService';
import { FetchError } from '@frontend/store/fetchError';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { ITotalWeightLossItem } from './TotalWeightLossTable';
import { MonitoringCard } from '../../../monitoring/MonitoringCard';
import { TotalWeightLossChartTooltip } from './TotalWeightLossChartTooltip';
import { TotalWeightLossMonitoringChartHeader } from './TotalWeightLossMonitoringChartHeader';
import { TotalWeightLossTable } from './TotalWeightLossTable';

type TotalWeightLossProps = Readonly<{
    process: Process<ServiceCode.BariPost>;
}>;

export function TotalWeightLoss(props: TotalWeightLossProps): JSX.Element | null {
    const { process } = props;
    const abortSignal = useAbortSignal();
    const [canReadTotalWeightLoss, setCanReadTotalWeightLoss] = useState(false);
    const [isEmpty, setIsEmpty] = useState(true);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [totalWeightLossMeasurements, setTotalWeightLossMeasurements] = useState<ITotalWeightLossItem[]>([]);
    const [totalWeightLossDistributionVanDerLaar, setTotalWeightLossDistributionVanDerLaar] = useState<
        ITotalWeightLossItem[]
    >([]);
    const [totalWeightLossDistributionOca, setTotalWeightLossDistributionOca] = useState<ITotalWeightLossItem[]>([]);
    const [warningText, setWarningText] = useState<string>();
    const [showOcaDistribution, setShowOcaDistribution] = useState(false);
    const weightBeforeSurgery = process.processData?.gewicht_voor_operatie;

    useEffect(() => {
        loadCanReadTotalWeightLoss(process.id);
    }, [process]);

    useEffect(() => {
        if (canReadTotalWeightLoss && weightBeforeSurgery) {
            loadTotalWeightLossData(process.id);
        }
    }, [canReadTotalWeightLoss, process.id, weightBeforeSurgery]);

    if (!canReadTotalWeightLoss) {
        return null;
    }

    return (
        <>
            <SectionHeader
                noGutterOnMobile={true}
                title="Total weight loss"
            />
            <MonitoringCard
                emptyTitle="Er zijn nog geen total weight loss-gegevens voor deze patiënt"
                isEmpty={!weightBeforeSurgery || (isEmpty && !warningText)}
                isLoading={isLoading}
            >
                <Card.Body>
                    <TotalWeightLossMonitoringChartHeader
                        showOcaDistribution={showOcaDistribution}
                        showToggleForOcaDistribution={totalWeightLossDistributionOca.length > 0}
                        titleWarningText={getTitleWarningText()}
                        toggleShowOcaDistribution={() => setShowOcaDistribution(!showOcaDistribution)}
                        warningText={warningText}
                        weightBeforeSurgery={weightBeforeSurgery}
                    />
                    <MonitoringChart
                        height={500}
                        items={getMonitoringChartItems()}
                        warningText={warningText}
                        XAxisLabel="Maanden na operatie"
                        XAxisTicks={getXAxisTicks()}
                        YAxisLabel="%TWL"
                    />
                </Card.Body>
                {!warningText && (
                    <MonitoringCardFooter
                        isClosedText="Toon tabel"
                        isOpen={isOpen}
                        isOpenText="Inklappen"
                        onClick={() => setIsOpen(!isOpen)}
                    >
                        <TotalWeightLossTable items={totalWeightLossMeasurements} />
                    </MonitoringCardFooter>
                )}
            </MonitoringCard>
        </>
    );

    async function loadCanReadTotalWeightLoss(processId: string): Promise<void> {
        const canRead = await canReadBariPostTotalWeightLoss(processId, abortSignal);
        setCanReadTotalWeightLoss(canRead);
    }

    function getXAxisTicks(): number[] | undefined {
        const monitoringChartItems = getMonitoringChartItems();
        if (monitoringChartItems.length > 0) {
            const lastMonitoringCharItem = monitoringChartItems[monitoringChartItems.length - 1];
            const maxMonthsAfterSurgery = Math.ceil(lastMonitoringCharItem.difference.months);
            const maxMonthsAfterSurgeryKeys = [...new Array(maxMonthsAfterSurgery).keys()];

            return [...maxMonthsAfterSurgeryKeys, maxMonthsAfterSurgeryKeys.length];
        }
    }

    async function loadTotalWeightLossData(processId: string): Promise<void> {
        try {
            setIsLoading(true);

            const totalWeightLoss = await getTotalWeightLoss(processId, abortSignal);

            if (totalWeightLoss.measurements.length <= 1) {
                return setIsEmpty(true);
            }

            setTotalWeightLossMeasurements(totalWeightLoss.measurements);
            setTotalWeightLossDistributionOca(totalWeightLoss.totalWeightLossDistributionOca);
            setTotalWeightLossDistributionVanDerLaar(totalWeightLoss.totalWeightLossDistributionVanDerLaar);

            return setIsEmpty(totalWeightLoss.measurements.length === 0);
        } catch (error) {
            if (error instanceof FetchError) {
                const errorText = getErrorText(error);
                setWarningText(errorText);
            } else {
                throw error;
            }
        } finally {
            setIsLoading(false);
        }
    }

    function getMonitoringChartItems(): IMonitoringChartItem[] {
        const distribution = showOcaDistribution
            ? totalWeightLossDistributionOca
            : totalWeightLossDistributionVanDerLaar;

        const measurementsAndDistribution = orderBy(
            [...getMeasurementsToShowInGraph(), ...distribution],
            ['date'],
            ['asc']
        );

        return measurementsAndDistribution.map((item) => ({
            date: item.date,
            difference: item.difference,
            median: item.median,
            minMaxFirstBandwidth: item.minMaxFirstBandwidth,
            minMaxSecondBandwidth: item.minMaxSecondBandwidth,
            shape: item.isFromFollowUp ? 'triangle' : 'circle',
            tooltipElement:
                typeof item.weight === 'number' && typeof item.totalWeightLoss === 'number' ? (
                    <TotalWeightLossChartTooltip
                        date={DateTime.from(item.date).format('YYYY-MM-DD')}
                        totalWeightLoss={item.totalWeightLoss}
                        weight={item.weight}
                    />
                ) : null,
            value: item.totalWeightLoss,
        }));
    }

    function getTitleWarningText(): string | undefined {
        const latestTotalWeightLossMeasurement = totalWeightLossMeasurements
            .filter((totalWeightLossItem) => totalWeightLossItem.totalWeightLoss)
            .at(-1);

        if (
            latestTotalWeightLossMeasurement?.totalWeightLossState === TotalWeightLossState.under25 ||
            latestTotalWeightLossMeasurement?.totalWeightLossState === TotalWeightLossState.over75
        ) {
            return 'Er is een alarm';
        }
    }

    function getErrorText(error: FetchError): string {
        if (error.apiErrorResponse?.errorCode === TotalWeightLossErrorCode.gender) {
            return `Het geslacht van deze patiënt is niet geregistreerd. Omdat de grafiek waarden laat zien ten opzichte van andere patiënten met hetzelfde geslacht, kunnen we geen gegevens laten zien. Neem contact op met de Ksyos polikliniek om dit te wijzigen.`;
        }
        throw error;
    }

    function getMeasurementsToShowInGraph() {
        if (showOcaDistribution) {
            return totalWeightLossMeasurements.filter((measurement) =>
                DateTime.from(measurement.date).isBefore(useVanDerLaarDistributionFromDate)
            );
        }

        return totalWeightLossMeasurements;
    }
}
